import { useEffect, useState } from "react";
import type { PatientSearchResult, TaskAssignable, TaskTypeView } from "@dentacore/shared-types";

import { atLocation } from "../api/client";
import { searchPatients } from "../api/patients";
import { createTask, readAssignable } from "../api/tasks";
import { useCan } from "../auth/use-can";
import { useShown } from "../shell/locations";
import { Modal } from "../ui/modal";
import styles from "./tasks.module.css";

/**
 * Add a task by hand (TK-D2, TK-D3): one of the clinic's types (TK-D14), a
 * title, what it is about, to a role — unclaimed — or straight to a person
 * working there (TK-D10, TK-D11), an optional due day and patient. With
 * several locations shown it asks which one (UR-D7); the task is created there.
 */
export function NewTaskDialog({
  types,
  onClose,
  onCreated,
}: {
  types: TaskTypeView[];
  onClose: () => void;
  onCreated: () => void;
}): React.JSX.Element {
  const shown = useShown();
  const seesPatients = useCan()("patients.view");
  const [place, setPlace] = useState<string>(shown.active ?? shown.ids[0] ?? "");
  const [who, setWho] = useState<TaskAssignable | null>(null);
  const [typeId, setTypeId] = useState(types[0]?.id ?? "");
  const [giveTo, setGiveTo] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState("");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<PatientSearchResult[]>([]);
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The types can arrive after the form opens: take the first then, so the
  // select never shows a type the form has not chosen.
  useEffect(() => {
    if (typeId === "" && types.length > 0) setTypeId(types[0]?.id ?? "");
  }, [typeId, types]);

  // The locations shown can arrive after the form opens: take the first then.
  useEffect(() => {
    if (place === "" && shown.ids.length > 0) setPlace(shown.active ?? shown.ids[0] ?? "");
  }, [place, shown.key, shown.active]);

  useEffect(() => {
    if (place === "") return;
    const controller = new AbortController();
    readAssignable(place, controller.signal)
      .then((next) => {
        setWho(next);
        // Keep a choice that is still offered here; drop one that is not (a
        // person who does not work at the newly chosen location).
        setGiveTo((was) => {
          const [kind, id] = was.split(":");
          const offered =
            kind === "role"
              ? next.roles.some((r) => r.id === id)
              : next.people.some((p) => p.id === id);
          return offered ? was : "";
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) setWho(null);
      });
    return () => controller.abort();
  }, [place]);

  useEffect(() => {
    if (!seesPatients || query.trim().length < 2 || patient !== null) {
      setFound([]);
      return;
    }
    const controller = new AbortController();
    const wait = window.setTimeout(() => {
      searchPatients({ q: query.trim(), limit: 6 }, controller.signal)
        .then((page) => setFound(page.patients))
        .catch(() => setFound([]));
    }, 250);
    return () => {
      window.clearTimeout(wait);
      controller.abort();
    };
  }, [query, patient, seesPatients]);

  const ready = typeId !== "" && title.trim() !== "" && giveTo !== "" && place !== "";

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!ready) return;
    const [kind, id] = giveTo.split(":");
    setBusy(true);
    setError(null);
    atLocation(place, () =>
      createTask({
        typeId,
        title: title.trim(),
        ...(description.trim() === "" ? {} : { description: description.trim() }),
        ...(kind === "role" ? { roleId: id } : { assigneeId: id }),
        // The end of the chosen day on this browser's clock.
        ...(due === "" ? {} : { dueAt: new Date(`${due}T18:00`).toISOString() }),
        ...(patient === null ? {} : { patientId: patient.id }),
      }),
    )
      .then(onCreated)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not add the task"),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      label="New task"
      onClose={onClose}
      panelClassName={`${styles.dialog} ${styles.dialogNarrow}`}
      testId="task-new"
    >
      <form onSubmit={submit}>
        <header className={styles.dialogHead}>
          <div className={styles.line}>
            <h2 className={styles.dialogTitle}>New task</h2>
            <span className={styles.grow} />
            <button type="button" className={styles.quiet} onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>
        <div className={styles.form}>
          {types.length === 0 && (
            <p className={`${styles.error} ${styles.full}`}>
              This clinic has no task types yet. Someone who sets up tasks can add them under Task
              types.
            </p>
          )}
          <label className={styles.field}>
            Type
            <select
              className={styles.filterSelect}
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              data-testid="task-new-type"
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Give to
            <select
              className={styles.filterSelect}
              value={giveTo}
              onChange={(e) => setGiveTo(e.target.value)}
              data-testid="task-new-to"
            >
              <option value="">Choose…</option>
              {who !== null && (
                <>
                  <optgroup label="A role (anyone in it can take it)">
                    {who.roles.map((r) => (
                      <option key={r.id} value={`role:${r.id}`}>
                        {r.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="A person working here">
                    {who.people.map((p) => (
                      <option key={p.id} value={`person:${p.id}`}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                </>
              )}
            </select>
          </label>
          <label className={`${styles.field} ${styles.full}`}>
            Title
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
              data-testid="task-new-title"
            />
          </label>
          <label className={`${styles.field} ${styles.full}`}>
            Description
            <textarea
              className={styles.textarea}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              data-testid="task-new-description"
            />
          </label>
          {seesPatients && (
            <div className={`${styles.field} ${styles.full}`}>
              Patient
              {patient === null ? (
                <>
                  <input
                    className={styles.input}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Optional — type a name or file number"
                    aria-label="Patient"
                  />
                  {found.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={styles.menuItem}
                      onClick={() => setPatient(p)}
                    >
                      {p.firstName} {p.lastName}
                      <span className={styles.menuHint}>{p.fileNumberDisplay}</span>
                    </button>
                  ))}
                </>
              ) : (
                <span className={styles.line}>
                  {patient.firstName} {patient.lastName}
                  <button
                    type="button"
                    className={`${styles.quiet} ${styles.small}`}
                    onClick={() => setPatient(null)}
                  >
                    Change
                  </button>
                </span>
              )}
            </div>
          )}
          <label className={styles.field}>
            Due
            <input
              type="date"
              className={styles.input}
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </label>
          {shown.many && (
            <label className={styles.field}>
              Location
              <select
                className={styles.filterSelect}
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                data-testid="task-new-location"
              >
                {shown.ids.map((id) => (
                  <option key={id} value={id}>
                    {shown.byId.get(id)?.name ?? id}
                  </option>
                ))}
              </select>
            </label>
          )}
          {error !== null && (
            <p className={`${styles.error} ${styles.full}`} role="alert">
              {error}
            </p>
          )}
        </div>
        <footer className={styles.dialogFoot}>
          <span className={styles.grow} />
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={!ready || busy}
            data-testid="task-new-create"
          >
            {busy ? "Adding…" : "Create task"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
